import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {IOSSwitch} from '../../../../components/styles/ToggleButton';
import {SponsorFormState} from '../task/cmpt/AddSponsorStep';
import type {
  IKeyPartnersProps,
  IParameterDetailsProp,
  IPartnerProps,
} from '../TnnCampaignCreationStepper';
import {
  TnnPartnerAmountTable,
  TnnPartnerAmountTotals,
} from './TnnPartnerAmountTable';
import {
  TNN_PARTNER_AMOUNT_FIELDS,
  TNN_PARTNER_PRODUCTS,
  sumPartnerAmount,
  sumPartnerShare,
  updatePartnerCompanyPrice,
} from './tnnPartnerUtils';
import {TnnReviewField, TnnReviewGrid, tnnValueOrDash} from './TnnReviewField';

const DetailsCard = ({
  title,
  children,
  hideTitle = false,
}: {
  title: string;
  children: React.ReactNode;
  hideTitle?: boolean;
}) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3.5,
      bgcolor: 'background.default',
      p: 3,
    }}>
    {!hideTitle ? (
      <Typography sx={{fontSize: 16, fontWeight: 600, mb: 2}}>
        {title}
      </Typography>
    ) : null}
    {children}
  </Box>
);

const TnnSlotMedia = ({
  file,
  url,
  trackName,
}: {
  file?: File;
  url: string;
  trackName: string;
}) => {
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    if (!file) {
      setFileUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setFileUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const previewUrl = fileUrl || url;
  return previewUrl ? (
    <MusicPreviewBtn
      id={trackName}
      url={previewUrl}
      track_name={trackName}
      track_artist={trackName}
    />
  ) : null;
};

const executionGridColumns =
  '32px minmax(120px, 1fr) 72px 72px minmax(160px, 1.4fr)';

const TnnExecutionGrid = ({
  children,
  header = false,
}: {
  children: React.ReactNode;
  header?: boolean;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: executionGridColumns,
      gap: 1.5,
      alignItems: 'center',
      ...(header
        ? {color: 'text.secondary', fontSize: 14, mb: 1}
        : {minHeight: 58}),
    }}>
    {children}
  </Box>
);

export const TnnExecutionSpots = ({
  parameter,
}: {
  parameter: IParameterDetailsProp;
}) => (
  <DetailsCard title='Executions/Spots'>
    {parameter.slot_details.length > 0 ? (
      <>
        <TnnExecutionGrid header>
          <Typography>#</Typography>
          <Typography>Label</Typography>
          <Typography>Weight</Typography>
          <Typography>Primary</Typography>
          <Typography>TNN Ad File</Typography>
        </TnnExecutionGrid>
        <Divider sx={{borderColor: 'divider', mb: 1}} />
        <Stack spacing={0}>
          {parameter.slot_details.map(slot => {
            const fileName =
              slot.file?.name ||
              slot.media_track?.file_name ||
              slot.fileName ||
              '—';
            const mediaUrl = slot.media_track?.url || slot.initial_url || '';
            return (
              <Stack key={slot.tnn_campaign_slot} spacing={0}>
                <TnnExecutionGrid>
                  <Typography>{slot.tnn_campaign_slot}</Typography>
                  <Typography noWrap title={slot.label}>
                    {tnnValueOrDash(slot.label)}
                  </Typography>
                  <Typography>{slot.tnn_campaign_weightage}</Typography>
                  <FormControlLabel
                    label=''
                    sx={{m: 0}}
                    control={
                      <IOSSwitch
                        checked={Boolean(slot.tnn_campaign_is_primary)}
                        disabled
                        inputProps={{
                          'aria-label': `Primary slot ${slot.tnn_campaign_slot}`,
                        }}
                      />
                    }
                  />
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    minWidth={0}>
                    <Typography
                      noWrap
                      title={fileName}
                      sx={{color: 'text.secondary', minWidth: 0}}>
                      {fileName}
                    </Typography>
                    <TnnSlotMedia
                      file={slot.file}
                      url={mediaUrl}
                      trackName={fileName}
                    />
                  </Stack>
                </TnnExecutionGrid>
                <Divider sx={{borderColor: 'divider'}} />
              </Stack>
            );
          })}
        </Stack>
      </>
    ) : (
      <Typography sx={{py: 2, color: 'text.secondary', fontSize: 14}}>
        No spots details available.
      </Typography>
    )}
  </DetailsCard>
);

const TnnReviewColumns = ({
  children,
  hasPartners,
  breakpoint = 'lg',
  topSpacing = false,
}: {
  children: React.ReactNode;
  hasPartners: boolean;
  breakpoint?: 'lg' | 'xl';
  topSpacing?: boolean;
}) => {
  const childrenArray = React.Children.toArray(children);
  const firstColumnWidth = hasPartners ? 7 : 12;
  const secondColumnWidth = hasPartners ? 5 : 12;

  return (
    <Grid
      container
      spacing={2}
      alignItems='flex-start'
      sx={topSpacing ? {mt: 2} : undefined}>
      {childrenArray.map((child, index) => (
        <Grid
          item
          key={index}
          xs={12}
          lg={
            breakpoint === 'lg'
              ? index === 0
                ? firstColumnWidth
                : secondColumnWidth
              : 12
          }
          xl={
            breakpoint === 'xl'
              ? index === 0
                ? firstColumnWidth
                : secondColumnWidth
              : undefined
          }
          sx={{
            minWidth:
              index === 1 && breakpoint === 'xl' && hasPartners ? 420 : 0,
          }}>
          {child}
        </Grid>
      ))}
    </Grid>
  );
};

const SponsorDetailsFields = ({
  sponsorForm,
}: {
  sponsorForm?: SponsorFormState;
}) =>
  sponsorForm ? (
    <TnnReviewGrid>
      <TnnReviewField label='Company Name' value={sponsorForm.companyName} />
      <TnnReviewField label='Main Contact' value={sponsorForm.mainContact} />
      <TnnReviewField label='Email' value={sponsorForm.mainContactEmail} />
      <TnnReviewField label='Phone' value={sponsorForm.mainContactPhone} />
      <TnnReviewField label='Category' value={sponsorForm.sponsorCategory} />
      <TnnReviewField
        label='Sponsorship Type'
        value={sponsorForm.sponsorshipType}
      />
    </TnnReviewGrid>
  ) : (
    <Typography sx={{color: 'text.secondary', fontSize: 14}}>
      Sponsor details are available in the sponsor record.
    </Typography>
  );

const CampaignDetailsFields = ({
  campaignName,
  campaignType,
  campaignDate,
  numberOfRuns,
  type,
  commercialLength,
  adSource,
  slots,
  manual,
  includeManual = true,
}: {
  campaignName: string;
  campaignType: string[];
  campaignDate: string;
  numberOfRuns: number;
  type: string;
  commercialLength: string;
  adSource: string;
  slots: number | null;
  manual: boolean;
  includeManual?: boolean;
}) => (
  <TnnReviewGrid>
    <TnnReviewField
      label='Campaign Name/Type'
      value={`${campaignName} / ${campaignType.join(', ')}`}
    />
    <TnnReviewField label='Number of Runs' value={numberOfRuns} />
    <TnnReviewField label='Campaign Parameter Date' value={campaignDate} />
    {type === 'H' ? (
      <TnnReviewField label='Commercial Length' value={commercialLength} />
    ) : null}
    {type === 'H' ? (
      <TnnReviewField label='Ad Source' value={adSource} />
    ) : null}
    <TnnReviewField
      label='No of Executions/Spots'
      value={slots != null ? slots : '—'}
    />
    {includeManual ? (
      <TnnReviewField label='Manual' value={manual ? 'Yes' : 'No'} />
    ) : null}
  </TnnReviewGrid>
);

const TnnReviewSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Stack spacing={2}>
    <Typography sx={{fontSize: 16, fontWeight: 600}}>{title}</Typography>
    {children}
  </Stack>
);

export type TnnReviewSummaryProps = {
  sponsorForm?: SponsorFormState;
  campaignName: string;
  adSource: string;
  commercialLength: string;
  type: string;
  campaignType: string[];
  parameter: IParameterDetailsProp;
  partners: IPartnerProps;
  setPartners?: React.Dispatch<React.SetStateAction<IPartnerProps>>;
  isReview?: boolean;
  campaignReviewLayout?: boolean;
  firstColumnContent?: React.ReactNode;
  hubspotLayout?: boolean;
};

export const TnnReviewSummary = ({
  sponsorForm,
  campaignName,
  adSource,
  commercialLength,
  type,
  campaignType,
  parameter,
  partners,
  setPartners,
  isReview = true,
  campaignReviewLayout = false,
  firstColumnContent,
  hubspotLayout = false,
}: TnnReviewSummaryProps) => {
  const updatePartnerPrice = useCallback(
    (productKey: IKeyPartnersProps, companyId: number, rawValue: string) => {
      if (!setPartners) return;
      setPartners(prev => ({
        ...prev,
        [productKey]: {
          ...prev[productKey],
          selected_companies: prev[productKey].selected_companies.map(
            company =>
              company.id === companyId
                ? updatePartnerCompanyPrice(company, rawValue)
                : company,
          ),
        },
      }));
    },
    [setPartners],
  );

  const campaignDate = useMemo(
    () =>
      parameter.start_date && parameter.end_date
        ? `${parameter.start_date.format(
            'MM-DD-YYYY',
          )} / ${parameter.end_date.format('MM-DD-YYYY')}`
        : '—',
    [parameter.end_date, parameter.start_date],
  );

  const partnerCards = campaignType
    .map(typeValue =>
      typeValue === 'GameTime'
        ? ('gametime' as IKeyPartnersProps)
        : typeValue === 'Neptune Radio'
        ? ('neptuneRadio' as IKeyPartnersProps)
        : null,
    )
    .filter((key): key is IKeyPartnersProps => key !== null);

  const partnerTotals = partnerCards.reduce(
    (totals, productKey) => {
      const companies = partners[productKey].selected_companies;
      return {
        totalPrice:
          totals.totalPrice +
          (sumPartnerAmount(companies, TNN_PARTNER_AMOUNT_FIELDS.price) ?? 0),
        totalShare: totals.totalShare + (sumPartnerShare(companies) ?? 0),
      };
    },
    {totalPrice: 0, totalShare: 0},
  );

  const partnerDetails = (
    <Stack spacing={2} sx={{minWidth: 0}}>
      {partnerCards.map(productKey => (
        <TnnPartnerAmountTable
          key={productKey}
          title={`${TNN_PARTNER_PRODUCTS[productKey].label} Partners`}
          partners={partners[productKey].selected_companies}
          editable={Boolean(setPartners)}
          onPriceChange={
            setPartners
              ? (companyId, value) =>
                  updatePartnerPrice(productKey, companyId, value)
              : undefined
          }
        />
      ))}
      <TnnPartnerAmountTotals
        price={partnerTotals.totalPrice}
        share={partnerTotals.totalShare}
        boxed
      />
    </Stack>
  );

  const campaignDetails = (
    <CampaignDetailsFields
      campaignName={campaignName}
      campaignType={campaignType}
      campaignDate={campaignDate}
      numberOfRuns={parameter.number_of_runs}
      type={type}
      commercialLength={commercialLength}
      adSource={adSource}
      slots={parameter.slots}
      manual={parameter.manual}
    />
  );

  return (
    <Stack sx={{width: '100%', minWidth: 0, pb: 2}}>
      {isReview ? (
        <Typography
          sx={{fontSize: 18, lineHeight: '20px', fontWeight: 600, mb: 2}}>
          Review
        </Typography>
      ) : null}

      {campaignReviewLayout ? (
        <TnnReviewColumns hasPartners={partnerCards.length > 0}>
          <Stack spacing={2} sx={{minWidth: 0}}>
            <DetailsCard title='Campaign Details'>
              <CampaignDetailsFields
                campaignName={campaignName}
                campaignType={campaignType}
                campaignDate={campaignDate}
                numberOfRuns={parameter.number_of_runs}
                type={type}
                commercialLength={commercialLength}
                adSource={adSource}
                slots={parameter.slots}
                manual={parameter.manual}
                includeManual={false}
              />
            </DetailsCard>
            <TnnExecutionSpots parameter={parameter} />
            {firstColumnContent ? (
              <Stack sx={{minWidth: 0}}>{firstColumnContent}</Stack>
            ) : null}
          </Stack>
          {partnerCards.length > 0 ? partnerDetails : null}
        </TnnReviewColumns>
      ) : hubspotLayout ? (
        <TnnReviewColumns hasPartners={partnerCards.length > 0}>
          <Stack spacing={2} sx={{minWidth: 0}}>
            <DetailsCard title='Sponsor & Campaign Details' hideTitle>
              <Stack spacing={3}>
                <TnnReviewSection title='Sponsor Details'>
                  <SponsorDetailsFields sponsorForm={sponsorForm} />
                </TnnReviewSection>
                <Divider sx={{borderColor: 'divider'}} />
                <TnnReviewSection title='Campaign Details'>
                  {campaignDetails}
                </TnnReviewSection>
              </Stack>
            </DetailsCard>
            {parameter.manual ? (
              <TnnExecutionSpots parameter={parameter} />
            ) : null}
          </Stack>
          {partnerCards.length > 0 ? partnerDetails : null}
        </TnnReviewColumns>
      ) : (
        <>
          <DetailsCard title='Sponsor Details'>
            <SponsorDetailsFields sponsorForm={sponsorForm} />
          </DetailsCard>
          <Divider sx={{my: 2, borderColor: 'divider'}} />
          <DetailsCard title='Campaign Details'>{campaignDetails}</DetailsCard>
          {parameter.manual === true ? (
            <TnnReviewColumns
              breakpoint='xl'
              hasPartners={partnerCards.length > 0}
              topSpacing>
              <TnnExecutionSpots parameter={parameter} />
              {partnerCards.length > 0 ? partnerDetails : null}
            </TnnReviewColumns>
          ) : partnerCards.length > 0 ? (
            <Stack sx={{mt: 2}}>{partnerDetails}</Stack>
          ) : null}
        </>
      )}
    </Stack>
  );
};
